import React from 'react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { EpisodeStatus } from '@/types/episode.types';
import { Clip } from '@/types/clip.types';

/**
 * Convert various date formats to Date object
 * Handles: Date, ISO string, number (ms or seconds), Firestore Timestamp objects
 * Returns null for invalid dates to prevent RangeError in date-fns format()
 */
const toDate = (value: unknown): Date | null => {
  if (!value) return null;

  let date: Date | null = null;

  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string') {
    date = new Date(value);
  } else if (typeof value === 'number') {
    // Assume Unix seconds if value is less than a reasonable ms timestamp
    date = value < 10000000000 ? new Date(value * 1000) : new Date(value);
  } else if (typeof value === 'object') {
    // Handle Firestore Timestamp objects (from WebSocket JSON)
    const obj = value as Record<string, unknown>;
    const seconds = (obj._seconds ?? obj.seconds) as number | undefined;
    if (typeof seconds === 'number') {
      date = new Date(seconds * 1000);
    }
  }

  // Validate the date - return null if invalid to prevent RangeError
  if (date && !isNaN(date.getTime())) {
    return date;
  }
  return null;
};

interface StatusBannerConfig {
  title: string;
  detail: string;
  dotColor: string;
  bgColor: string;
  borderColor: string;
  titleColor: string;
}

interface StatusBannerProps {
  status: EpisodeStatus;
  clips: Clip[];
  publishedAt?: Date | number | null;
  errorMessage?: string | null;
}

export const StatusBanner: React.FC<StatusBannerProps> = ({
  status,
  clips,
  publishedAt,
  errorMessage,
}) => {
  const clipCount = clips.length;

  // Compute counts directly from clips array for real-time WebSocket updates
  // Scripts ready = clips at script_ready status or beyond
  const scriptsReady = clips.filter((c) =>
    ['script_ready', 'script_approved', 'audio_queued', 'generating_audio', 'audio_ready', 'audio_approved'].includes(c.status)
  ).length;

  // Audios ready = clips at audio_ready status or beyond
  const audiosReady = clips.filter((c) =>
    ['audio_ready', 'audio_approved'].includes(c.status)
  ).length;

  const getStatusBannerConfig = (): StatusBannerConfig => {
    // Color scheme:
    // Gray (#6b7280) - Draft/New
    // Amber (#f59e0b) - Clips Added, Generating
    // Orange (#f97316) - Scripts Ready for Review
    // Cyan (#00c8b4) - Scripts Approved
    // Teal (#14b8a6) - Audio Ready/Approved
    // Blue (#3b82f6) - Merged
    // Green (#22c55e) - Published
    switch (status) {
      case EpisodeStatus.DRAFT:
        return clipCount === 0
          ? {
              title: 'New Episode',
              detail: 'Add clips to get started',
              dotColor: 'bg-gray-500',
              bgColor: 'rgba(107, 114, 128, 0.15)',
              borderColor: 'rgba(107, 114, 128, 0.3)',
              titleColor: 'text-[var(--pm-text-muted)]',
            }
          : {
              title: 'Clips Added',
              detail: `${clipCount} clip${clipCount !== 1 ? 's' : ''} ready for script generation`,
              dotColor: 'bg-amber-500',
              bgColor: 'rgba(245, 158, 11, 0.15)',
              borderColor: 'rgba(245, 158, 11, 0.3)',
              titleColor: 'text-amber-500',
            };
      case EpisodeStatus.GENERATING_SCRIPTS:
        return {
          title: 'Generating Scripts',
          detail: `${scriptsReady} of ${clipCount} scripts complete`,
          dotColor: 'bg-amber-500 animate-pulse',
          bgColor: 'rgba(245, 158, 11, 0.15)',
          borderColor: 'rgba(245, 158, 11, 0.3)',
          titleColor: 'text-amber-500',
        };
      case EpisodeStatus.SCRIPTS_PENDING_REVIEW:
        return {
          title: 'Scripts Ready for Review',
          detail: `${scriptsReady} of ${clipCount} scripts complete`,
          dotColor: 'bg-orange-500',
          bgColor: 'rgba(249, 115, 22, 0.15)',
          borderColor: 'rgba(249, 115, 22, 0.3)',
          titleColor: 'text-orange-500',
        };
      case EpisodeStatus.SCRIPTS_APPROVED:
        return {
          title: 'Scripts Approved',
          detail: 'Ready to generate audio',
          dotColor: 'bg-[#00c8b4]',
          bgColor: 'rgba(0, 200, 180, 0.12)',
          borderColor: 'rgba(0, 200, 180, 0.25)',
          titleColor: 'text-[#00c8b4]',
        };
      case EpisodeStatus.GENERATING_AUDIO:
        return {
          title: 'Generating Audio',
          detail: `${audiosReady} of ${clipCount} audio clips complete`,
          dotColor: 'bg-amber-500 animate-pulse',
          bgColor: 'rgba(245, 158, 11, 0.15)',
          borderColor: 'rgba(245, 158, 11, 0.3)',
          titleColor: 'text-amber-500',
        };
      case EpisodeStatus.AUDIO_PENDING_REVIEW:
        return {
          title: 'Audio Ready for Review',
          detail: `${audiosReady} of ${clipCount} audio clips complete`,
          dotColor: 'bg-teal-500',
          bgColor: 'rgba(20, 184, 166, 0.15)',
          borderColor: 'rgba(20, 184, 166, 0.3)',
          titleColor: 'text-teal-500',
        };
      case EpisodeStatus.AUDIO_APPROVED:
        return {
          title: 'Audio Approved',
          detail: 'Ready to merge episode',
          dotColor: 'bg-teal-500',
          bgColor: 'rgba(20, 184, 166, 0.12)',
          borderColor: 'rgba(20, 184, 166, 0.25)',
          titleColor: 'text-teal-500',
        };
      case EpisodeStatus.MERGING:
        return {
          title: 'Merging Episode',
          detail: 'Combining audio files...',
          dotColor: 'bg-blue-500 animate-pulse',
          bgColor: 'rgba(59, 130, 246, 0.12)',
          borderColor: 'rgba(59, 130, 246, 0.25)',
          titleColor: 'text-blue-500',
        };
      case EpisodeStatus.MERGED:
        return {
          title: 'Episode Ready',
          detail: 'Ready to publish',
          dotColor: 'bg-blue-500',
          bgColor: 'rgba(59, 130, 246, 0.12)',
          borderColor: 'rgba(59, 130, 246, 0.25)',
          titleColor: 'text-blue-500',
        };
      case EpisodeStatus.PUBLISHING:
        return {
          title: 'Publishing',
          detail: 'Uploading to Podbean...',
          dotColor: 'bg-blue-500 animate-pulse',
          bgColor: 'rgba(59, 130, 246, 0.12)',
          borderColor: 'rgba(59, 130, 246, 0.25)',
          titleColor: 'text-blue-500',
        };
      case EpisodeStatus.PUBLISHED: {
        const pubDate = toDate(publishedAt);
        return {
          title: 'Published',
          detail: pubDate
            ? `Published ${format(pubDate, 'MMM d, yyyy')}`
            : 'Live on Podbean',
          dotColor: 'bg-green-500',
          bgColor: 'rgba(34, 197, 94, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.25)',
          titleColor: 'text-green-500',
        };
      }
      case EpisodeStatus.PUBLISHING_FAILED:
        return {
          title: 'Publishing Failed',
          detail: errorMessage || 'Click retry to try again',
          dotColor: 'bg-red-500',
          bgColor: 'rgba(239, 68, 68, 0.12)',
          borderColor: 'rgba(239, 68, 68, 0.25)',
          titleColor: 'text-red-500',
        };
      case EpisodeStatus.FAILED:
      case EpisodeStatus.SCRIPT_FAILED:
      case EpisodeStatus.AUDIO_FAILED:
        return {
          title: 'Failed',
          detail: errorMessage || 'Check clips for errors',
          dotColor: 'bg-red-500',
          bgColor: 'rgba(239, 68, 68, 0.12)',
          borderColor: 'rgba(239, 68, 68, 0.25)',
          titleColor: 'text-red-500',
        };
      case EpisodeStatus.ARCHIVED:
        return {
          title: 'Archived',
          detail: 'This episode has been archived',
          dotColor: 'bg-gray-400',
          bgColor: 'rgba(113, 113, 122, 0.15)',
          borderColor: 'rgba(113, 113, 122, 0.3)',
          titleColor: 'text-[var(--pm-text-faint)]',
        };
      default:
        return {
          title: 'Unknown Status',
          detail: '',
          dotColor: 'bg-gray-400',
          bgColor: 'rgba(113, 113, 122, 0.15)',
          borderColor: 'rgba(113, 113, 122, 0.3)',
          titleColor: 'text-[var(--pm-text-faint)]',
        };
    }
  };

  const config = getStatusBannerConfig();

  return (
    <div className="flex items-center gap-1 mt-2 py-1 pb-2 mx-1">
      <span className="font-medium text-foreground">Clips</span>
        {clips.length > 0 && (
        <>
          <Badge variant="secondary">{clips.length}</Badge>
        </>
      )}
      <div
        className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-md"
        style={{
          border: `1px solid ${config.borderColor}`,
        }}
      >
        <span className={`w-3 h-3 rounded-full shrink-0 ${config.dotColor}`} />
        <span className="text-[var(--pm-text-muted)] text-sm">{config.detail}</span>
      </div>
    </div>
  );
};

export default StatusBanner;
